// Cloud pricing reference rates (USD). Approximated public list-price averages
// across common SKUs. Used for illustrative comparisons — not billing accuracy.
// Live rates from AWS Bulk Pricing API and Azure Retail Prices API overlay
// the static table when available via the cloudPricing Deno function.

export type StorageTier = 'standard' | 'cold' | 'archive'
export type ProviderId = 'aws' | 'azure' | 'gcp' | 'oci'

export interface Provider {
  id: ProviderId
  name: string
  shortName: string
  color: string
  accent: string
  computeService: string
  storageService: string
  serverlessService: string
}

export const PROVIDERS: Provider[] = [
  {
    id: 'aws',
    name: 'Amazon Web Services',
    shortName: 'AWS',
    color: '#FF9900',
    accent: 'rgba(255, 153, 0, 0.15)',
    computeService: 'EC2',
    storageService: 'S3 / EBS',
    serverlessService: 'Lambda',
  },
  {
    id: 'azure',
    name: 'Microsoft Azure',
    shortName: 'Azure',
    color: '#38BDF8',
    accent: 'rgba(56, 189, 248, 0.15)',
    computeService: 'Virtual Machines',
    storageService: 'Blob Storage',
    serverlessService: 'Functions',
  },
  {
    id: 'gcp',
    name: 'Google Cloud',
    shortName: 'GCP',
    color: '#34D399',
    accent: 'rgba(52, 211, 153, 0.15)',
    computeService: 'Compute Engine',
    storageService: 'Cloud Storage',
    serverlessService: 'Cloud Functions',
  },
  {
    id: 'oci',
    name: 'Oracle Cloud Infrastructure',
    shortName: 'Oracle',
    color: '#FB7185',
    accent: 'rgba(251, 113, 133, 0.15)',
    computeService: 'Compute',
    storageService: 'Object Storage',
    serverlessService: 'Functions',
  },
]

interface RateTable {
  // per vCPU-hour
  cpuHour: number
  // per GB-RAM-hour
  ramHour: number
  // per GB-month
  storage: { standard: number, cold: number, archive: number }
  // per GB egress
  egress: number
  // per 1M invocations
  invocations: number
}

const RATES: Record<ProviderId, RateTable> = {
  aws: {
    cpuHour: 0.0232,
    ramHour: 0.00305,
    storage: { standard: 0.023, cold: 0.0125, archive: 0.004 },
    egress: 0.09,
    invocations: 0.20,
  },
  azure: {
    cpuHour: 0.0218,
    ramHour: 0.00298,
    storage: { standard: 0.0184, cold: 0.01, archive: 0.00099 },
    egress: 0.087,
    invocations: 0.20,
  },
  gcp: {
    cpuHour: 0.021,
    ramHour: 0.00282,
    storage: { standard: 0.020, cold: 0.010, archive: 0.0012 },
    egress: 0.085,
    invocations: 0.40,
  },
  oci: {
    cpuHour: 0.0185,
    ramHour: 0.00255,
    storage: { standard: 0.0255, cold: 0.0102, archive: 0.0026 },
    egress: 0.0085,
    invocations: 0.20,
  },
}

export interface CalcInputs {
  cpuCores: number
  ramGb: number
  computeHours: number
  storageGb: number
  storageTier: StorageTier
  egressGb: number
  invocations: number // raw count
}

export interface CostBreakdown {
  compute: number
  storage: number
  network: number
  serverless: number
  total: number
}

export interface ProviderResult extends CostBreakdown {
  provider: Provider
}

// Optional live-rate override map — keyed by ProviderId.
// If a provider's entry is present its values override the static RATES table.
export type LiveRateOverrides = Partial<Record<ProviderId, Partial<{
  cpuHour: number
  ramHour: number
  storage: { standard: number; cold: number; archive: number }
  egress: number
  invocations: number
}>>>

export function calcProviderCost(
  p: ProviderId,
  i: CalcInputs,
  overrides?: LiveRateOverrides,
): CostBreakdown {
  const base = RATES[p]
  const ov   = overrides?.[p] ?? {}
  const r = {
    cpuHour:     ov.cpuHour     ?? base.cpuHour,
    ramHour:     ov.ramHour     ?? base.ramHour,
    storage:     ov.storage     ?? base.storage,
    egress:      ov.egress      ?? base.egress,
    invocations: ov.invocations ?? base.invocations,
  }
  const compute   = (i.cpuCores * r.cpuHour + i.ramGb * r.ramHour) * i.computeHours
  const storage   = i.storageGb * r.storage[i.storageTier]
  const network   = i.egressGb  * r.egress
  const serverless = (i.invocations / 1_000_000) * r.invocations
  const total = compute + storage + network + serverless
  return { compute, storage, network, serverless, total }
}

export function calcAll(
  i: CalcInputs,
  overrides?: LiveRateOverrides,
): ProviderResult[] {
  return PROVIDERS.map(provider => ({
    provider,
    ...calcProviderCost(provider.id, i, overrides),
  })).sort((a, b) => a.total - b.total)
}

export function fmtUSD(n: number, opts?: { compact?: boolean }): string {
  if (opts?.compact && n >= 10_000) {
    return `$${(n / 1000).toFixed(1)}k`
  }
  if (n >= 100) return `$${n.toFixed(0)}`
  if (n >= 10) return `$${n.toFixed(2)}`
  return `$${n.toFixed(2)}`
}

export function fmtNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`
  return `${n}`
}
